import React, { useEffect, useMemo, useRef, useState } from "react";
import { loadPortfolioGraphSeries } from "../services/portfolio/loadPortfolioGraphSeries";
import "./PortfolioOverviewGraph.css";

const GRAPH_EDGE_PAD = 1;

function graphPointMetric(point, viewMode) {
  if (viewMode === "change") {
    return Number(point?.change) || 0;
  }
  return Number(point?.price) || 0;
}

function resolveSparklineYAxis(minValue, maxValue) {
  const range = maxValue - minValue;
  if (!Number.isFinite(range) || Number.isNaN(range) || range <= 0) {
    return { minValue, maxValue, yAxisRange: 1 };
  }
  return { minValue, maxValue, yAxisRange: range };
}

function yForSparklineValue(value, minValue, yAxisRange, graphAreaHeight) {
  return graphAreaHeight - ((value - minValue) / yAxisRange) * graphAreaHeight;
}

function xAtGraphIndex(graphData, index, graphWidth) {
  const innerW = graphWidth - GRAPH_EDGE_PAD * 2;
  return GRAPH_EDGE_PAD + (index / (graphData.length - 1)) * innerW;
}

function buildPortfolioSparklinePath(graphData, viewMode, graphWidth, graphAreaHeight) {
  if (!graphData || graphData.length <= 1) {
    return { pathData: "", areaPath: "" };
  }

  const values = graphData.map((d) => graphPointMetric(d, viewMode));
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const yAxisRange = resolveSparklineYAxis(minValue, maxValue).yAxisRange;

  let pathData = "";
  const firstY = yForSparklineValue(values[0], minValue, yAxisRange, graphAreaHeight);
  const firstX = xAtGraphIndex(graphData, 0, graphWidth);
  pathData += `M ${firstX} ${firstY}`;

  for (let i = 1; i < graphData.length; i += 1) {
    const currentValue = values[i];
    const currentX = xAtGraphIndex(graphData, i, graphWidth);
    const currentY = yForSparklineValue(
      currentValue,
      minValue,
      yAxisRange,
      graphAreaHeight
    );
    const prevX = xAtGraphIndex(graphData, i - 1, graphWidth);
    const prevY = yForSparklineValue(
      values[i - 1],
      minValue,
      yAxisRange,
      graphAreaHeight
    );
    const controlPointX1 = prevX + (currentX - prevX) * 0.4;
    const controlPointX2 = currentX - (currentX - prevX) * 0.4;
    pathData += ` C ${controlPointX1} ${prevY}, ${controlPointX2} ${currentY}, ${currentX} ${currentY}`;
  }

  const lastX = xAtGraphIndex(graphData, graphData.length - 1, graphWidth);
  const areaPath = `${pathData} L ${lastX} ${graphAreaHeight} L ${firstX} ${graphAreaHeight} Z`;

  const crossesZero = minValue < 0 && maxValue > 0;
  const zeroLineY = crossesZero
    ? yForSparklineValue(0, minValue, yAxisRange, graphAreaHeight)
    : null;

  return {
    pathData,
    areaPath,
    minValue,
    maxValue,
    yAxisRange,
    zeroLineY,
    values,
  };
}

const TIMEFRAME_OPTIONS = [
  { label: "1D", value: "1d" },
  { label: "1W", value: "5d" },
  { label: "1M", value: "1mo" },
  { label: "3M", value: "3mo" },
  { label: "YTD", value: "ytd" },
  { label: "1Y", value: "1y" },
  { label: "5Y", value: "5y" },
  { label: "Max", value: "max" },
];

/**
 * Web portfolio graph — uses the same data pipeline as apps/mobile/components/PortfolioGraph.js.
 */
const PortfolioOverviewGraph = ({
  userId,
  portfolioId = null,
  timeFrame: timeFrameProp = "1d",
  viewMode = "value",
  height = 220,
  showTimeFramePicker = true,
  showZeroLine = true,
  exchangeRateFromUSD = 1,
  onPeriodChangeUpdate,
  onPortfolioValueUpdate,
}) => {
  const [timeFrame, setTimeFrame] = useState(timeFrameProp);
  const [graphData, setGraphData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [containerWidth, setContainerWidth] = useState(600);
  const containerRef = useRef(null);

  useEffect(() => {
    setTimeFrame(timeFrameProp);
  }, [timeFrameProp]);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!userId) {
        setGraphData([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const result = await loadPortfolioGraphSeries({
          userId,
          portfolioId,
          timeFrame,
          exchangeRateFromUSD,
        });
        if (cancelled) return;

        setGraphData(result.graphData || []);
        onPortfolioValueUpdate?.(result.portfolioValueUsd);
        onPeriodChangeUpdate?.({
          changeUsdInternal: result.changeUsdInternal,
          percentageChange: result.percentageChange,
          currencyChangeDisplay: result.currencyChangeDisplay,
        });
      } catch (loadError) {
        if (!cancelled) {
          console.error("PortfolioOverviewGraph:", loadError);
          setError("Failed to load portfolio chart");
          setGraphData([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [
    userId,
    portfolioId,
    timeFrame,
    exchangeRateFromUSD,
    onPeriodChangeUpdate,
    onPortfolioValueUpdate,
  ]);

  const graphLayout = useMemo(() => {
    const graphWidth = Math.max(containerWidth, 120);
    const graphAreaHeight = Math.max(height - (showTimeFramePicker ? 36 : 8), 80);
    return { graphWidth, graphAreaHeight };
  }, [containerWidth, height, showTimeFramePicker]);

  const sparkline = useMemo(
    () =>
      buildPortfolioSparklinePath(
        graphData,
        viewMode,
        graphLayout.graphWidth,
        graphLayout.graphAreaHeight
      ),
    [graphData, viewMode, graphLayout.graphWidth, graphLayout.graphAreaHeight]
  );

  const lineColor =
    sparkline.values?.length > 1 &&
    sparkline.values[sparkline.values.length - 1] >= sparkline.values[0]
      ? "#22C55E"
      : "#EF4444";

  const activeTimeframeLabel =
    TIMEFRAME_OPTIONS.find((option) => option.value === timeFrame)?.label ||
    timeFrame.toUpperCase();

  return (
    <div
      ref={containerRef}
      className="portfolio-overview-graph"
      style={{ minHeight: height }}
    >
      {loading ? (
        <div className="portfolio-overview-graph__state">Loading portfolio chart…</div>
      ) : error ? (
        <div className="portfolio-overview-graph__state portfolio-overview-graph__state--error">
          {error}
        </div>
      ) : !graphData.length ? (
        <div className="portfolio-overview-graph__state">
          No portfolio data yet. Add holdings to see your chart.
        </div>
      ) : (
        <svg
          width="100%"
          height={graphLayout.graphAreaHeight}
          viewBox={`0 0 ${graphLayout.graphWidth} ${graphLayout.graphAreaHeight}`}
          preserveAspectRatio="none"
          className="portfolio-overview-graph__svg"
        >
          <defs>
            <linearGradient
              id="portfolio-overview-fill"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor={lineColor} stopOpacity="0.35" />
              <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
            </linearGradient>
          </defs>

          {showZeroLine && sparkline.zeroLineY != null ? (
            <line
              x1={0}
              y1={sparkline.zeroLineY}
              x2={graphLayout.graphWidth}
              y2={sparkline.zeroLineY}
              stroke="rgba(255,255,255,0.35)"
              strokeWidth="1"
            />
          ) : null}

          <path d={sparkline.areaPath} fill="url(#portfolio-overview-fill)" />
          <path
            d={sparkline.pathData}
            fill="none"
            stroke={lineColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}

      {showTimeFramePicker ? (
        <div className="portfolio-overview-graph__timeframes">
          {TIMEFRAME_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={
                timeFrame === option.value
                  ? "portfolio-overview-graph__tf portfolio-overview-graph__tf--active"
                  : "portfolio-overview-graph__tf"
              }
              onClick={() => setTimeFrame(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : (
        <div className="portfolio-overview-graph__badge">{activeTimeframeLabel}</div>
      )}
    </div>
  );
};

export default PortfolioOverviewGraph;
